import React, { useState } from 'react';
import {
  FiX, FiFileText, FiRefreshCw, FiAlertCircle, FiCopy, FiCheck,
  FiGlobe, FiUser, FiMessageSquare,
} from 'react-icons/fi';
import { generatePresalesProposal } from '../../api/ai';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  nodes: any[];
  edges: any[];
  provider: string;
  environment: string;
  architectureName?: string;
  projectName?: string;
  costs?: any;
}

function markdownToHtml(md: string): string {
  const fencedBlocks: string[] = [];
  let processed = md.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
    const idx = fencedBlocks.length;
    fencedBlocks.push(`<pre class="code-block"><code>${code.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</code></pre>`);
    return `\x00BLOCK${idx}\x00`;
  });
  processed = processed
    .replace(/^#{4}\s+(.+)$/gm, '<h4>$1</h4>')
    .replace(/^#{3}\s+(.+)$/gm, '<h3>$1</h3>')
    .replace(/^#{2}\s+(.+)$/gm, '<h2>$1</h2>')
    .replace(/^#{1}\s+(.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr/>')
    .replace(/^\|(.+)\|$/gm, (row) => {
      const cells = row.split('|').filter((_, i, a) => i > 0 && i < a.length - 1);
      return '<tr>' + cells.map(c => c.trim().match(/^[-:]+$/) ? '' : `<td>${c.trim()}</td>`).join('') + '</tr>';
    })
    .replace(/(<tr>.*<\/tr>\n?)+/g, m => `<table>${m}</table>`)
    .replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>')
    .replace(/^[-*]\s+(.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
    .replace(/\n\n+/g, '</p><p>')
    .replace(/\n/g, '<br/>');
  processed = processed.replace(/\x00BLOCK(\d+)\x00/g, (_, i) => fencedBlocks[parseInt(i)]);
  return `<p>${processed}</p>`;
}

const PresalesModal: React.FC<Props> = ({
  isOpen, onClose, nodes, edges, provider, environment, architectureName, projectName, costs,
}) => {
  const [step, setStep]           = useState<'form' | 'result'>('form');
  const [clientName, setClient]   = useState('');
  const [context, setContext]     = useState('');
  const [language, setLanguage]   = useState<'fr' | 'en'>('fr');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [proposal, setProposal]   = useState('');
  const [metadata, setMetadata]   = useState<{ tokensUsed: number; durationMs: number } | null>(null);
  const [copied, setCopied]       = useState(false);

  const providerName = { azure: 'Azure', aws: 'AWS', gcp: 'Google Cloud' }[provider] || provider;

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await generatePresalesProposal({
        nodes, edges, provider, environment, architectureName, projectName,
        clientName: clientName.trim() || undefined,
        context: context.trim() || undefined,
        costs,
        language,
      });
      setProposal(result.proposal);
      setMetadata({ tokensUsed: result.metadata.tokensUsed, durationMs: result.metadata.durationMs });
      setStep('result');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(proposal).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleExportPdf = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const html = markdownToHtml(proposal);
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Proposition — ${architectureName || providerName}</title>
    <style>
      body{font-family:Georgia,'Times New Roman',serif;padding:40px;max-width:800px;margin:0 auto;color:#111;font-size:14px;line-height:1.7}
      h1{font-size:22px;font-weight:700;color:#1e1b4b;border-bottom:2px solid #4f46e5;padding-bottom:8px;margin:24px 0 12px}
      h2{font-size:18px;font-weight:700;color:#312e81;margin:20px 0 8px}
      h3{font-size:15px;font-weight:600;color:#3730a3;margin:14px 0 6px}
      h4{font-size:13px;font-weight:600;margin:10px 0 4px}
      p{margin:8px 0}strong{font-weight:700}em{font-style:italic}
      ul{margin:6px 0;padding-left:24px}li{margin:3px 0}
      table{width:100%;border-collapse:collapse;margin:12px 0}
      th{background:#eef2ff;text-align:left;padding:8px 12px;font-size:13px;border:1px solid #c7d2fe}
      td{padding:8px 12px;font-size:13px;border:1px solid #e0e7ff}
      blockquote{border-left:3px solid #4f46e5;padding:4px 12px;margin:8px 0;color:#4338ca;background:#eef2ff;border-radius:0 4px 4px 0}
      hr{border:none;border-top:1px solid #e5e7eb;margin:20px 0}
      code{font-family:monospace;background:#f3f4f6;padding:1px 4px;border-radius:3px;font-size:12px}
      pre.code-block{background:#1e1b4b;color:#c7d2fe;padding:12px;border-radius:6px;overflow-x:auto;font-size:12px}
      @media print{body{padding:20px}}
    </style></head><body>${html}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  const handleDownloadMd = () => {
    const blob = new Blob([proposal], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `proposition-${(architectureName || projectName || 'cloud').replace(/\s+/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FiFileText className="text-violet-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-800">Assistant Presales</h2>
            <span className="text-xs text-gray-400 font-normal">Proposition commerciale {providerName}</span>
          </div>
          <div className="flex items-center gap-2">
            {step === 'result' && (
              <button onClick={() => { setStep('form'); setProposal(''); setError(null); }}
                className="text-xs text-gray-500 hover:text-violet-600 transition-colors px-2 py-1 rounded hover:bg-gray-50">
                ← Modifier
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── Step 1: Form ── */}
          {step === 'form' && (
            <div className="space-y-5 max-w-lg mx-auto">
              <p className="text-sm text-gray-600">
                Personnalisez la proposition commerciale générée à partir de l'architecture canvas.
              </p>

              {/* Client */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  <FiUser size={12} className="inline mr-1" />Nom du client (optionnel)
                </label>
                <input
                  type="text"
                  value={clientName}
                  onChange={e => setClient(e.target.value)}
                  placeholder="ex : Société Dupont SA"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400"
                />
              </div>

              {/* Context */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  <FiMessageSquare size={12} className="inline mr-1" />Contexte / besoin client (optionnel)
                </label>
                <textarea
                  value={context}
                  onChange={e => setContext(e.target.value)}
                  rows={4}
                  placeholder="ex : Migration depuis une infrastructure on-prem, objectif haute disponibilité et réduction des coûts de 30%..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400 resize-none"
                />
              </div>

              {/* Language */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  <FiGlobe size={12} className="inline mr-1" />Langue du document
                </label>
                <div className="flex gap-2">
                  {(['fr', 'en'] as const).map(lang => (
                    <button key={lang}
                      onClick={() => setLanguage(lang)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
                        language === lang
                          ? 'border-violet-400 bg-violet-50 text-violet-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}>
                      {lang === 'fr' ? '🇫🇷 Français' : '🇬🇧 English'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Architecture summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-1">Architecture utilisée :</p>
                <p className="text-sm font-medium text-gray-700">{architectureName || projectName || 'Architecture sans nom'}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {nodes.filter(n => n.data?.type).length} ressource{nodes.filter(n => n.data?.type).length !== 1 ? 's' : ''} · {providerName} · {environment}
                  {costs?.totalMonthly ? ` · ~${costs.totalMonthly.toFixed(0)} ${costs.currency || 'USD'}/mois` : ''}
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm flex items-center gap-2">
                  <FiAlertCircle size={14} />{error}
                </div>
              )}

              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                style={{ background: loading ? '#9ca3af' : 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
                {loading
                  ? <><FiRefreshCw size={15} className="animate-spin" /> Génération en cours…</>
                  : <><FiFileText size={15} /> Générer la proposition</>}
              </button>
            </div>
          )}

          {/* ── Step 2: Result ── */}
          {step === 'result' && proposal && (
            <div>
              <div
                className="prose max-w-none text-sm text-gray-800 leading-relaxed"
                style={{
                  '--tw-prose-headings': '#1e1b4b',
                  '--tw-prose-h1': '#1e1b4b',
                } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: markdownToHtml(proposal) }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'result' && proposal && (
          <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-between gap-4 bg-gray-50 rounded-b-2xl">
            <div className="text-xs text-gray-400 flex items-center gap-3">
              {metadata && (
                <>
                  <span>{metadata.tokensUsed.toLocaleString()} tokens</span>
                  <span>{(metadata.durationMs / 1000).toFixed(1)}s</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleCopy}
                className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-violet-600 transition-colors px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-violet-300">
                {copied ? <><FiCheck size={12} className="text-green-500" /> Copié</> : <><FiCopy size={12} /> Copier MD</>}
              </button>
              <button onClick={handleDownloadMd}
                className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-violet-600 transition-colors px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-violet-300">
                ↓ .md
              </button>
              <button onClick={handleExportPdf}
                className="flex items-center gap-1.5 text-xs text-white px-3 py-1.5 rounded-lg transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
                Export PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PresalesModal;
